/* eslint-disable lines-between-class-members */

import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
const ParticleEmitterCtor = (typeof globalThis !== "undefined" && (globalThis.ParticleEmitterNode || globalThis.ParticleEmitter)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const PARTICLE_PROPS = [
  "src",
  "startColor",
  "middleColor", 
  "endColor",
  "startOpacity",
  "middleOpacity",
  "endOpacity",
  "colorCurve",
  "sizeCurve",
  "startSize",
  "endSize",
  "sizeRandomness",
  "ageRandomness",
  "lifetime",
  "lifetimeRandomness",
  "particleCount",
  "startVelocity",
  "endVelocity",
  "velocityCurve",
  "angularVelocity"
];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class ParticleEmitterEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    // Helper function to ensure valid THREE.Color objects
    const ensureValidColor = (value) => {
      if (value && value.isColor) {
        return value;
      }
      return new THREE.Color(value);
    };

    PARTICLE_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          const isColorProp = prop === "startColor" || prop === "middleColor" || prop === "endColor";
          
          if (isColorProp) {
            const validColor = ensureValidColor(v);
            this._values.set(prop, validColor);
            
            if (this.sourceObject?.[prop]?.isColor) {
              this.sourceObject[prop].copy(validColor);
            } else if (this.sourceObject?.[prop]?.set) {
              this.sourceObject[prop].set(validColor);
            } else {
              this.sourceObject[prop] = validColor;
            }
            
            const desc = this._originalDescriptors.get(prop);
            if (desc?.set) {
              desc.set.call(this.sourceObject, validColor);
            }
          } else if ((prop === "startVelocity" || prop === "endVelocity") && this.sourceObject?.[prop]?.set) {
            // Special handling for Vector3 values
            try {
              if (Array.isArray(v) && v.length >= 3) {
                this.sourceObject[prop].set(v[0], v[1], v[2]);
              } else if (v && typeof v === "object" && v.x !== undefined) {
                this.sourceObject[prop].set(v.x, v.y, v.z);
              }
              this._values.set(prop, this.sourceObject[prop]);
            } catch {
              /* ignore */
            }
          } else if (this._originalDescriptors.get(prop)?.set) {
            try {
              this._originalDescriptors.get(prop).set.call(this.sourceObject, v);
              this._values.set(prop, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              this.sourceObject[prop] = v;
              this._values.set(prop, v);
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      
      // For color properties, ensure the initial value is also a valid THREE.Color
      const isColorProp = prop === "startColor" || prop === "middleColor" || prop === "endColor";
      if (isColorProp) {
        initial = ensureValidColor(initial);
        
        if (this.sourceObject?.[prop]?.isColor) {
          this.sourceObject[prop].copy(initial);
        } else if (this.sourceObject?.[prop]?.set) {
          this.sourceObject[prop].set(initial);
        } else {
          this.sourceObject[prop] = initial;
        }
      }
      
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class ParticleEmitterAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const COMPONENT = "particle-emitter";
    const _pendingUpdates = {};
    this._pendingUpdates = _pendingUpdates;

    const _flushPending = () => {
      if (!this.sourceObject?.el) return;

      if (Object.keys(_pendingUpdates).length > 0) {
        const current = {
          ...(this.sourceObject.el.getAttribute(COMPONENT) || {})
        };
        Object.assign(current, _pendingUpdates);
        this.sourceObject.el.setAttribute(COMPONENT, current);
        Object.keys(_pendingUpdates).forEach(
          key => delete _pendingUpdates[key]
        );
      }
    };

    if (this.sourceObject?.el) {
      this.sourceObject.el.addEventListener("componentinitialized", e => {
        if (e?.detail?.name === COMPONENT) {
          _flushPending();
        }
      });
    }

    // Helpers
    const setComponentProp = (key, val) => {
      if (!this.sourceObject?.el) return;

      const attr = this.sourceObject.el.getAttribute(COMPONENT);
      if (!attr) {
        // Component not initialized yet, store in pending
        _pendingUpdates[key] = val;
      } else {
        const current = { ...attr };
        current[key] = val;
        this.sourceObject.el.setAttribute(COMPONENT, current);
      }
    };

    const getComponentProp = key => {
      const attr = this.sourceObject?.el?.getAttribute(COMPONENT);
      if (attr) {
        return attr[key];
      }

      // Fall back to pending updates when component isn't initialized
      return _pendingUpdates[key];
    };

    // particle-emitter component props ------------------------------------
    PARTICLE_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getComponentProp(prop),
        set: v => setComponentProp(prop, v)
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of PARTICLE_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new ParticleEmitterAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new ParticleEmitterEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

export class ParticleEmitterElement extends ScriptableObject {
  static scriptableType = "particle-emitter";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      el.setAttribute("particle-emitter", { ...initial });

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real ParticleEmitterNode and upgrade to scriptable
    const node = createParticleEmitterElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public particle emitter props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      
      // deep-clone where necessary to avoid shared refs
      if ((prop === "startColor" || prop === "middleColor" || prop === "endColor")) {
        if (val?.isColor) {
          this[prop] = val.clone();
        } else {
          this[prop] = new THREE.Color(val);
        }
      } else if ((prop === "startVelocity" || prop === "endVelocity") && val?.isVector3) {
        this[prop] = val.clone();
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of PARTICLE_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of PARTICLE_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      // Follow the same pattern as other implementations
      if ((p === "startColor" || p === "middleColor" || p === "endColor")) {
        if (v?.isColor) {
          init[p] = v.clone();
        } else {
          init[p] = new THREE.Color(v);
        }
      } else if ((p === "startVelocity" || p === "endVelocity") && v?.isVector3) {
        init[p] = v.clone();
      } else if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    const dest = new ParticleEmitterElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor ParticleEmitterNode instance to Scriptable, attach adapter and proxies
function makeEditorParticleEmitterNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || ParticleEmitterCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: ParticleEmitterElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = ParticleEmitterElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new ParticleEmitterEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new ParticleEmitterEditorAdapter(node, descriptors);
  }

  for (const prop of PARTICLE_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  // Ensure ParticleEmitterElement's enhanced `clone` logic is used for
  // editor-created particle emitter nodes.
  try {
    if (node.clone !== ParticleEmitterElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: ParticleEmitterElement.prototype.clone,
        writable: true,
        configurable: true
      });
    }
  } catch {
    node.clone = ParticleEmitterElement.prototype.clone;
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
PARTICLE_PROPS.forEach(prop => {
  Object.defineProperty(ParticleEmitterElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createParticleEmitterElementEditor(initial = {}) {
  const ParticleEmitterCtor = (typeof globalThis !== "undefined" && (globalThis.ParticleEmitterNode || globalThis.ParticleEmitter)) || undefined;
  
  if (typeof ParticleEmitterCtor !== "function") {
    throw new Error("ParticleEmitterElementEditor: ParticleEmitterNode constructor not found.");
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new ParticleEmitterCtor(editor);
  } catch (e) {
    throw new Error(`ParticleEmitterElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorParticleEmitterNodeScriptable(node);

  // Helper function to ensure valid THREE.Color objects for initial values
  const ensureValidInitialColor = (value) => {
    if (value && value.isColor) {
      return value;
    }
    return new THREE.Color(value);
  };

  for (const k of Object.keys(initial)) {
    if (k in node) {
      // Handle special cases for initial color values
      const isColorProp = k === "startColor" || k === "middleColor" || k === "endColor";
      if (isColorProp) {
        const validColor = ensureValidInitialColor(initial[k]);
        
        if (node[k]?.isColor) {
          node[k].copy(validColor);
        } else if (node[k]?.set) {
          node[k].set(validColor);
        } else {
          node[k] = validColor;
        }
      } else {
        node[k] = initial[k];
      }
    }
  }
  
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("particle-emitter", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== ParticleEmitterElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return ParticleEmitterElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorParticleEmitterNodeScriptable(source);

  return source;
});

/* Named exports expected by external consumers */
export { ParticleEmitterAFrameAdapter, ParticleEmitterEditorAdapter };

/* Optional: nicer DevTools label */
Object.defineProperty(ParticleEmitterElement.prototype, Symbol.toStringTag, { value: "ParticleEmitterElement" });
